import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useDialog } from "@/components/Dialogs";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Copy, Share2, ArrowUp, ArrowDown, ListOrdered } from "lucide-react";
import { ContentReactions, ReactedBy, ReportLink, Comments, ListBody, VIS_LABEL, errText, when } from "@/components/ContentBits";

const BLANK = { title: "", description: "", ranked: false, visibility: "private", comments_off: false, items: [] };
const IDEAS = ["My top 5 musicals", "Shows that made me cry", "Best West End date nights", "Shows I'd see again tomorrow"];

const toForm = (l) => ({
  title: l.title, description: l.description || "", ranked: !!l.ranked, visibility: l.visibility || "private", comments_off: !!l.comments_off,
  items: l.items.map((i) => ({ key: i.show_id, show_id: i.is_custom ? "" : i.show_id, name: i.name, venue: i.venue || "", custom: !!i.is_custom, reason: i.reason || "" })),
});

function ListEditor({ initial, id, ShowPicker, onClose, onSaved }) {
  const [f, setF] = useState(initial ? toForm(initial) : BLANK);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const setItem = (i, patch) => set({ items: f.items.map((it, n) => (n === i ? { ...it, ...patch } : it)) });
  const move = (i, d) => {
    const a = [...f.items]; const j = i + d;
    if (j < 0 || j >= a.length) return;
    [a[i], a[j]] = [a[j], a[i]]; set({ items: a });
  };
  const pick = (s) => {
    setPicking(false);
    if (s.custom) { set({ items: [...f.items, { key: `new-${Date.now()}`, show_id: "", name: "", venue: "", custom: true, reason: "" }] }); return; }
    if (f.items.some((it) => it.show_id === s.id)) { toast.error(`${s.name} is already in your list.`); return; }
    set({ items: [...f.items, { key: s.id, show_id: s.id, name: s.name, venue: s.venue || "", custom: false, reason: "" }] });
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { title: f.title, description: f.description, ranked: f.ranked, visibility: f.visibility, comments_off: f.comments_off,
      items: f.items.map((it) => (it.custom ? { custom_name: it.name, custom_venue: it.venue, reason: it.reason } : { show_id: it.show_id, reason: it.reason })) };
    try {
      const r = id ? await api.put(`/lists/${id}`, body) : await api.post("/lists", body);
      toast.success(id ? "List saved" : "List created");
      onSaved(r.data);
    } catch (err) { toast.error(errText(err, "Could not save that. Please try again.")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="List">
      <form onSubmit={save} className="flex max-h-[92vh] w-full max-w-xl flex-col rounded-t-2xl border border-border bg-card p-5 sm:rounded-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold">{id ? "Edit list" : "New list"}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-1.5 hover:bg-secondary"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 flex-1 space-y-4 overflow-y-auto pr-1">
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="list-title">Title</label>
            <input id="list-title" data-testid="list-title" maxLength={80} value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. My top 5 musicals" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            {!f.title && !id && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {IDEAS.map((t) => <button key={t} type="button" onClick={() => set({ title: t })} className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-[#C89B3C]/60">{t}</button>)}
              </div>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="list-desc">About this list <span className="font-normal text-muted-foreground">(optional)</span></label>
            <input id="list-desc" maxLength={200} value={f.description} onChange={(e) => set({ description: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="list-ranked" checked={f.ranked} onChange={(e) => set({ ranked: e.target.checked })} /> Number the shows (a ranking)</label>

          <div>
            <p className="mb-2 text-sm font-medium">Shows <span className="font-normal text-muted-foreground">({f.items.length} of 20)</span></p>
            <ul className="space-y-3">
              {f.items.map((it, i) => (
                <li key={it.key} className="rounded-xl border border-border bg-background/50 p-3" data-testid="edit-item">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      {it.custom ? (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <input aria-label="Show name" data-testid="item-name" value={it.name} maxLength={80} onChange={(e) => setItem(i, { name: e.target.value })} placeholder="Show name" className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm" />
                          <input aria-label="Theatre" value={it.venue} maxLength={80} onChange={(e) => setItem(i, { venue: e.target.value })} placeholder="Theatre (optional)" className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm" />
                        </div>
                      ) : (<p className="font-medium">{f.ranked ? `${i + 1}. ` : ""}{it.name}{it.venue && <span className="block text-xs font-normal text-muted-foreground">{it.venue}</span>}</p>)}
                    </div>
                    <div className="flex flex-shrink-0 gap-0.5">
                      <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} className="rounded p-1 hover:bg-secondary disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                      <button type="button" aria-label="Move down" disabled={i === f.items.length - 1} onClick={() => move(i, 1)} className="rounded p-1 hover:bg-secondary disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                      <button type="button" aria-label="Remove from list" onClick={() => set({ items: f.items.filter((_, n) => n !== i) })} className="rounded p-1 hover:bg-secondary hover:text-red-400"><X className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <input aria-label="Why" data-testid="item-reason" value={it.reason} maxLength={140} onChange={(e) => setItem(i, { reason: e.target.value })} placeholder="One line on why (optional)" className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm" />
                </li>
              ))}
            </ul>
            {f.items.length < 20 && (
              <button type="button" data-testid="list-add-show" onClick={() => setPicking(true)} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#C89B3C]/60 px-4 py-1.5 text-sm font-medium text-[hsl(var(--gold))] hover:bg-[#C89B3C]/10"><Plus className="h-4 w-4" /> Add a show</button>
            )}
          </div>

          <fieldset>
            <legend className="mb-1 text-sm font-medium">Who can see this list?</legend>
            <div className="space-y-1.5 text-sm">
              {Object.entries(VIS_LABEL).map(([v, label]) => (
                <label key={v} className="flex items-center gap-2"><input type="radio" name="list-vis" data-testid={`list-vis-${v}`} checked={f.visibility === v} onChange={() => set({ visibility: v })} /> {label}</label>
              ))}
            </div>
            {f.visibility !== "private" && (
              <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="list-comments-on" checked={!f.comments_off} onChange={(e) => set({ comments_off: !e.target.checked })} /> Let my friends comment</label>
            )}
          </fieldset>
        </div>
        <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm hover:bg-secondary">Cancel</button>
          <button type="submit" data-testid="list-save" disabled={busy} className="rounded-full bg-[#C89B3C] px-6 py-2 text-sm font-semibold text-black disabled:opacity-60">{busy ? "Saving…" : "Save list"}</button>
        </div>
      </form>
      {picking && <ShowPicker onPick={pick} onClose={() => setPicking(false)} />}
    </div>
  );
}

export function ListCard({ list, mine, canReact = true, canReport = true, canComment = true, pinned = false, onEdit, onDelete, onChange }) {
  const link = `${window.location.origin}/list/${list.id}`;
  const [sharing, setSharing] = useState(false);
  const setVis = async (v) => {
    if (v === list.visibility) return;
    setSharing(true);
    try {
      const body = { title: list.title, description: list.description || "", ranked: !!list.ranked, visibility: v, comments_off: !!list.comments_off,
        items: list.items.map((it) => (it.is_custom ? { custom_name: it.name, custom_venue: it.venue || "", reason: it.reason || "" } : { show_id: it.show_id, reason: it.reason || "" })) };
      const r = await api.put(`/lists/${list.id}`, body);
      onChange({ ...list, ...r.data });
      toast.success(v === "private" ? "Now only visible to you" : v === "friends" ? "Shared with your friends" : "Anyone with the link can see it");
    } catch (err) { toast.error(errText(err, "Could not change that. Please try again.")); }
    finally { setSharing(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copied"); } catch { toast.error("Could not copy. Select the link and copy it instead."); } };
  return (
    <article className="rounded-2xl border border-border bg-card p-5" data-testid="list-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          {pinned && <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-[#C89B3C]" data-testid="pinned-badge">📌 Pinned</p>}
          <h3 className="font-serif text-xl font-semibold leading-tight">{list.title}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {mine ? VIS_LABEL[list.visibility] : <>by <Link to={`/u/${list.username}`} className="underline">{list.username}</Link></>} · {list.items.length} show{list.items.length === 1 ? "" : "s"} · {when(list.updated_at)}
            {mine && list.hidden && <span className="ml-2 rounded bg-red-500/20 px-1.5 py-0.5 text-red-300" data-testid="hidden-flag">Hidden by us</span>}
          </p>
          {list.description && <p className="mt-1 text-sm text-muted-foreground">{list.description}</p>}
        </div>
        {mine && (
          <div className="flex flex-shrink-0 items-center gap-3 text-sm">
            <button type="button" onClick={() => onEdit(list)} data-testid="list-edit" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /> Edit</button>
            <button type="button" onClick={() => onDelete(list)} data-testid="list-delete" className="inline-flex items-center gap-1 text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
          </div>
        )}
      </div>
      <ListBody list={list} />
      {mine && (
        <div className="mt-4 rounded-xl border border-border bg-background/60 p-3" data-testid="list-share">
          <p className="flex items-center gap-1.5 text-sm font-medium"><Share2 className="h-4 w-4 text-[#C89B3C]" /> Share this list</p>
          <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Who can see this list">
            {Object.entries(VIS_LABEL).map(([v, label]) => (
              <button key={v} type="button" disabled={sharing} aria-pressed={list.visibility === v} data-testid={`list-share-${v}`} onClick={() => setVis(v)}
                className={`rounded-full border px-3 py-1 text-sm ${list.visibility === v ? "border-[#C89B3C] bg-[#C89B3C] font-semibold text-black" : "border-border hover:bg-secondary"}`}>{label}</button>
            ))}
          </div>
          {list.visibility === "private"
            ? <p className="mt-2 text-xs text-muted-foreground">Only you can see this list. Choose “My friends” to let your friends read and comment on it, or “Anyone with the link” to share it with anyone.</p>
            : <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span>{list.visibility === "friends" ? "Your accepted friends can see it on their Lists tab and by this link." : "Anyone who has the link can see it."}</span>
                <button type="button" onClick={copy} data-testid="list-copy" className="inline-flex items-center gap-1 text-sm text-[#C89B3C] underline"><Copy className="h-3.5 w-3.5" /> Copy link</button>
              </div>}
        </div>
      )}
      {mine
        ? <><ContentReactions kind="list" item={list} canReact={false} onChange={onChange} /><ReactedBy item={list} onRemoved={(rid) => onChange({ ...list, reacted_by: list.reacted_by.filter((r) => r.id !== rid) })} /></>
        : <><ContentReactions kind="list" item={list} canReact={canReact} onChange={onChange} />{canReport && <div className="mt-3"><ReportLink kind="list" id={list.id} /></div>}</>}
      {canComment && list.visibility !== "private" && <Comments kind="list" id={list.id} count={list.comment_count || 0} onCount={(n) => n !== (list.comment_count || 0) && onChange({ ...list, comment_count: n })} />}
    </article>
  );
}

export default function ListsTab({ ShowPicker }) {
  const dialog = useDialog();
  const [lists, setLists] = useState(null);
  const [friends, setFriends] = useState([]);
  const [editing, setEditing] = useState(null);   // null | {list|null}

  const load = useCallback(() => {
    api.get("/lists").then((r) => setLists(r.data)).catch(() => setLists([]));
    api.get("/friends/lists").then((r) => setFriends(r.data)).catch(() => setFriends([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (l) => {
    if (!(await dialog.confirm({ title: "Delete this list?", body: `"${l.title}" will be deleted for good, along with any comments on it.`, confirmLabel: "Delete", danger: true }))) return;
    try { await api.delete(`/content/list/${l.id}`); toast.success("List deleted"); load(); }
    catch { toast.error("Could not delete that. Please try again."); }
  };
  const patch = (setter) => (u) => setter((a) => a.map((x) => (x.id === u.id ? { ...x, ...u } : x)));

  return (
    <div className="space-y-8" data-testid="lists-tab">
      {dialog.node}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Your lists</h2>
          <p className="text-sm text-muted-foreground">Top 5s, favourites and themes. Each show gets a line on why. Share them with friends or by link.</p>
        </div>
        <button type="button" data-testid="new-list" onClick={() => setEditing({ list: null })} className="inline-flex items-center gap-1.5 rounded-full bg-[#C89B3C] px-5 py-2.5 text-sm font-semibold text-black"><Plus className="h-4 w-4" /> New list</button>
      </div>

      {!lists ? <p className="text-muted-foreground">Loading…</p> : lists.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center" data-testid="lists-empty">
          <ListOrdered className="mx-auto h-8 w-8 text-[#C89B3C]" />
          <p className="mt-3 font-serif text-xl font-semibold">Make your first list</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Try “My top 5 musicals”, “Shows that made me cry” or “Best West End date nights”.</p>
        </div>
      ) : (
        <div className="space-y-5">{lists.map((l) => <ListCard key={l.id} list={l} mine onEdit={(x) => setEditing({ list: x })} onDelete={remove} onChange={patch(setLists)} />)}</div>
      )}

      {friends.length > 0 && (
        <section data-testid="friends-lists">
          <h2 className="font-serif text-2xl font-semibold">From your friends</h2>
          <div className="mt-4 space-y-5">{friends.map((l) => <ListCard key={l.id} list={l} onChange={patch(setFriends)} />)}</div>
        </section>
      )}

      {editing && <ListEditor initial={editing.list} id={editing.list?.id} ShowPicker={ShowPicker} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}